import { Truck, TruckStatus } from '../models/truck';
import { ManifestStatus } from '../models/manifest';
import { Icon } from '../common/commonModels';
import { UploadState } from '../models/document';

export enum StatusTileState {
  truckOpen,
  manifestingInitiated, // truck closed but no manifest id yet
  manifestingInProgress,
  userActionNeeded,
  uploadInProgress,
  uploadFailure,
  manifestingFailed,
  manifestingSuccessful,
  inconsistentManifestingState,
}

export enum TruckManifestStatus {
  Ready = 'ready',
  InProgress = 'inProgress',
  Success = 'success',
  Error = 'error',
  UserActionNeeded =  'userActionNeeded',
}

export function getStateTileStateFromTruck(truck: Truck): StatusTileState {
  if (truck.status === TruckStatus.OPEN) {
    return StatusTileState.truckOpen;
  }
  if (truck.status === TruckStatus.CLOSED && !truck.manifest) {
    return StatusTileState.manifestingInitiated;
  }
  if (truck.status === TruckStatus.CLOSED && truck.manifest && truck.manifest.status === ManifestStatus.IN_PROGRESS) {
    return StatusTileState.manifestingInProgress;
  }

  if (truck.status === TruckStatus.CLOSED && truck.manifest && truck.manifest.status === ManifestStatus.FAILURE) {
    return StatusTileState.manifestingFailed;
  }

  if (truck.status === TruckStatus.CLOSED && truck.manifest && truck.manifest.status === ManifestStatus.SUCCESS) {
    if (truck.manifest.documents) {
      for (const document of truck.manifest.documents) {
        if (document.manualUploadState === UploadState.UPLOAD_REQUIRED) {
          return StatusTileState.userActionNeeded;
        }
        if (document.manualUploadState === UploadState.UPLOADING) {
          return StatusTileState.uploadInProgress;
        }
        if (document.manualUploadState === UploadState.FAILURE) {
          return StatusTileState.uploadFailure;
        }
      }
    }
    return StatusTileState.manifestingSuccessful;
  }

  return StatusTileState.inconsistentManifestingState;
}

export function getTruckManifestStatus(truck: Truck): TruckManifestStatus {
  const status = getStateTileStateFromTruck(truck);
  switch (status) {
    case StatusTileState.truckOpen:
      return TruckManifestStatus.Ready;
    case StatusTileState.manifestingInitiated:
    case StatusTileState.manifestingInProgress:
    case StatusTileState.inconsistentManifestingState:
    case StatusTileState.uploadInProgress:
      return TruckManifestStatus.InProgress;
    case StatusTileState.manifestingSuccessful:
      return TruckManifestStatus.Success;
    case StatusTileState.userActionNeeded:
      return TruckManifestStatus.UserActionNeeded;
    case StatusTileState.manifestingFailed:
    case StatusTileState.uploadFailure:
    default:
      return TruckManifestStatus.Error;
  }
}

export function getStatusTileProperties(
  truckManifestStatus: TruckManifestStatus,
): { title: string, iconClassName: Icon} {
  switch (truckManifestStatus) {
    case TruckManifestStatus.Ready:
      return {
        title: 'Load shipments',
        iconClassName: Icon.Load,
      };
    case TruckManifestStatus.InProgress:
      return {
        title: 'Manifest in progress',
        iconClassName: Icon.Progress,
      };
    case TruckManifestStatus.Success:
      return {
        title: 'Manifest successful',
        iconClassName: Icon.Success,
      };
    case TruckManifestStatus.UserActionNeeded:
      return {
        title: 'Action required',
        iconClassName: Icon.Action,
      };
    case TruckManifestStatus.Error:
    default:
      return {
        title: 'Manifest issues',
        iconClassName: Icon.Error,
      };
  }
}

export function isUUID(value: string) {
  const regex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
  return !!value.match(regex);
}
